import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type PropsWithChildren } from "react";
import { type CollectOrderLine } from "@kanpla/api-contract";
import { type AllergyMatcher } from "@kanpla/allergens";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";
import { type AllergenName } from "~/api/products/types";
import { T } from "@repo/transifex";
import { collectLineDeclaration } from "~/hooks/use-collect-allergy-matcher";
import { AllergensIcons } from "~/components/allergens";

const OrderListSkeleton = () => {
  return Array.from({ length: 4 }).map((_, index) => (
    <div
      // eslint-disable-next-line react/no-array-index-key -- skeleton
      key={index}
      className="flex h-20 flex-row items-center justify-between gap-2 bg-interface-neutral-2 p-10"
    >
      <div
        className={cn("h-10 animate-pulse rounded-md bg-interface-neutral-4", {
          "w-1/3": index === 0,
          "w-1/2": index === 1,
          "w-2/5": index === 2,
          "w-1/4": index === 3
        })}
      />
    </div>
  ));
};

const orderList = cva("flex flex-col overflow-hidden rounded-2xl", {
  variants: {
    variant: {
      collect: "gap-1",
      menu: "border border-interface-neutral-3"
    }
  }
});

type OrdersListProps = PropsWithChildren<{
  isLoading?: boolean;
}> &
  VariantProps<typeof orderList>;

export const OrdersList = ({
  children,
  isLoading,
  variant = "collect"
}: OrdersListProps) => {
  return (
    <div className={orderList({ variant })}>
      {isLoading ? <OrderListSkeleton /> : null}
      {isLoading ? null : children}
    </div>
  );
};

const item = cva(
  "flex w-full flex-row items-center justify-between gap-2 p-6 text-interface-neutral-5",
  {
    variants: {
      variant: {
        collect: "bg-interface-neutral-2",
        menu: "border-b border-interface-neutral-3 bg-interface-neutral-0"
      },
      selected: {
        true: "bg-interface-positive-1 font-semibold text-interface-positive-4",
        false: "font-normal"
      }
    }
  }
);

type ItemProps = PropsWithChildren<{
  name: string;
  description?: string;
  isSelected: boolean;
  onClick: () => void;
  productAllergens: AllergenName[];
  matchAllergies: AllergyMatcher;
  options: CollectOrderLine["options"];
  price?: string;
  /** Formatted per-unit deposit of the product itself, already included in `price`. */
  deposit?: string;
  /** Formatted deposit of each picked option by option id, already included in `price`. */
  variantDeposits?: Record<string, string>;
  type?: "regular" | "variant";
}> &
  VariantProps<typeof item>;

const Item = ({
  name,
  description,
  isSelected,
  onClick,
  productAllergens,
  matchAllergies,
  options,
  price,
  deposit,
  variantDeposits,
  type = "regular",
  variant = "collect"
}: ItemProps) => {
  const conflict = matchAllergies(
    collectLineDeclaration(productAllergens, options)
  );
  const matchingAllergens = conflict
    ? [...conflict.contains, ...conflict.mayContain]
    : [];

  const isAllergic = conflict !== null;
  const isBlockedOnMenu = conflict?.action === "block" && variant === "menu";

  return (
    <button
      disabled={isBlockedOnMenu}
      className={cn(item({ variant, selected: isSelected }))}
      onClick={onClick}
      type="button"
    >
      <div className="flex flex-1 flex-col items-start justify-start gap-1">
        <div className="flex w-full flex-wrap items-center justify-start gap-2">
          <span
            className={cn("max-w-full break-words text-left text-2xl", {
              "text-red-500": isAllergic,
              "line-through": isBlockedOnMenu
            })}
          >
            {name}
          </span>

          <AllergensIcons
            allergens={productAllergens}
            highlightAllergens={matchingAllergens}
          />
        </div>

        {description ? (
          <span className="font-light italic">{description}</span>
        ) : null}

        {deposit && isSelected ? (
          <span className="text-base font-normal text-interface-neutral-5">
            <T _str="+{deposit} Deposit Fee" deposit={deposit} />
          </span>
        ) : null}

        <ItemOptions
          options={options}
          matchingAllergens={matchingAllergens}
          deposits={isSelected ? variantDeposits : undefined}
        />

        {price && isSelected ? (
          <span className="font-semibold">{price}</span>
        ) : null}
      </div>

      {isAllergic ? (
        <span className="font-normal text-red-500">
          <T _str="️Contains matching allergens" />
        </span>
      ) : null}

      {isBlockedOnMenu ? null : (
        <>
          {type === "variant" && !isSelected ? (
            <span className="mr-2 italic text-interface-neutral-5">
              <T _str="Select options" />
            </span>
          ) : null}
          <span
            className={cn(
              "size-10 rounded-full flex items-center justify-center",
              {
                "bg-interface-positive-2": isSelected,
                "bg-interface-neutral-3 drop-shadow-md": !isSelected
              }
            )}
          >
            {isSelected ? (
              <FontAwesomeIcon
                icon={faCheck}
                className="text-2xl text-interface-neutral-0"
              />
            ) : null}
          </span>
        </>
      )}
    </button>
  );
};

OrdersList.Item = Item;

type CollectedItemProps = {
  name: string;
  collectedAt: string;
  options: CollectOrderLine["options"];
  productAllergens: AllergenName[];
};

const CollectedItem = ({
  name,
  collectedAt,
  options,
  productAllergens
}: CollectedItemProps) => {
  return (
    <div className="flex flex-row items-center justify-between bg-interface-neutral-2 p-6">
      <div className="flex flex-col ">
        <div className="flex w-full flex-wrap items-center justify-start gap-2">
          <p className="text-2xl text-interface-positive-4">{name}</p>

          <AllergensIcons allergens={productAllergens} />
        </div>

        <ItemOptions options={options} />
      </div>

      <p className="text-2xl text-interface-neutral-5">{collectedAt}</p>
    </div>
  );
};

type ItemOptionsProps = {
  options: CollectOrderLine["options"];
  matchingAllergens?: string[];
  /** Formatted deposit by option id, shown under the option it belongs to. */
  deposits?: Record<string, string>;
};

const ItemOptions = ({
  options,
  matchingAllergens = [],
  deposits = {}
}: ItemOptionsProps) => {
  return (
    <div className="flex flex-1 flex-col items-start gap-1 text-base font-normal text-interface-neutral-5">
      {options.map(({ id, name, amount, allergens }) => {
        const isAllergicToOption = allergens.some((allergen) =>
          matchingAllergens.includes(allergen)
        );
        const deposit = deposits[id];

        return (
          <div
            key={name}
            className="flex flex-row flex-wrap items-center justify-start gap-2"
          >
            <span
              className={cn({ "text-red-500": isAllergicToOption })}
            >{`${amount}x ${name}`}</span>

            <AllergensIcons
              allergens={allergens}
              highlightAllergens={matchingAllergens}
              allergenClassName="text-interface-neutral-5 text-xs"
            />

            {deposit ? (
              <span className="w-full pl-4">
                <T _str="+{deposit} Deposit Fee" deposit={deposit} />
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

OrdersList.CollectedItem = CollectedItem;
